<template>
  <el-dialog
    :title="fileType === 'image' ? '选择背景图' : fileType === 'portrait' ? '选择立绘' : fileType === 'cover' ? '选择封面' : '选择音乐'"
    :visible="show"
    width="680px"
    append-to-body
    @close="handleClose"
    @open="handleOpen"
  >
    <div v-loading="loading" class="resource-uploader">
      <!-- Existing resources grid -->
      <div v-if="resources.length > 0" class="resource-grid">
        <div
          v-for="item in resources"
          :key="item.id"
          class="resource-item"
          :class="{ selected: selectedId === item.id }"
          @click="handleSelect(item)"
        >
          <div class="resource-thumb" :class="{ 'resource-thumb--music': fileType === 'music' }">
            <img v-if="fileType !== 'music'" :src="item.oss_url" class="thumb-image" />
            <div v-else class="thumb-music" @click.stop="togglePlay(item)">
              <i :class="playingId === item.id ? 'el-icon-video-pause' : 'el-icon-video-play'" class="play-icon"></i>
              <i class="el-icon-headset"></i>
            </div>
            <i class="el-icon-delete delete-btn" @click.stop="handleDelete(item)"></i>
          </div>
          <div class="resource-name" :title="item.display_name">{{ item.display_name }}</div>
        </div>
      </div>
      <div v-else-if="!loading" class="empty-tip">暂无资源</div>

      <!-- Divider -->
      <div class="upload-divider">
        <span>或上传新文件</span>
      </div>

      <!-- Upload new file -->
      <div class="upload-area">
        <el-upload
          :accept="fileType === 'music' ? 'audio/*,video/*' : 'image/*'"
          :show-file-list="false"
          :http-request="handleUpload"
          :before-upload="handleBeforeUpload"
          multiple
          drag
        >
          <i class="el-icon-upload"></i>
          <div class="el-upload__text">将文件拖到此处，或<em>点击上传</em>（支持多选）</div>
        </el-upload>
      </div>

      <!-- 上传进度列表 -->
      <div v-if="uploadQueue.length > 0" class="upload-progress-list">
        <div v-for="(item, idx) in uploadQueue" :key="idx" class="upload-progress-item">
          <span class="upload-file-name">{{ item.name }}</span>
          <el-progress
            :percentage="item.progress"
            :status="item.status === 'success' ? 'success' : item.status === 'error' ? 'exception' : undefined"
            :stroke-width="14"
            :text-inside="true"
            style="flex: 1;"
          />
        </div>
      </div>

      <!-- Selected image preview -->
      <div v-if="fileType !== 'music' && selectedResource" class="selected-preview">
        <img :src="selectedResource.oss_url" class="preview-image" />
        <div class="preview-name">{{ selectedResource.display_name }}</div>
      </div>
    </div>

    <span slot="footer">
      <el-button @click="handleClose">取消</el-button>
      <el-button
        type="primary"
        :disabled="selectedId === null || isUploading"
        @click="handleConfirm"
      >确定</el-button>
    </span>
  </el-dialog>
</template>

<script lang="ts">
import { Component, Prop, Watch, Vue } from 'vue-property-decorator'
import { getResources, uploadResource, uploadResourceWithProgress, deleteResource, ResourceMapping } from '@/api/resource'
import { checkUploadSize } from '@/constants/upload'

@Component
export default class ResourceUploader extends Vue {
  @Prop({ default: false }) readonly show!: boolean
  @Prop({ required: true }) readonly gameId!: number
  @Prop({ default: 'image' }) readonly fileType!: 'image' | 'music' | 'portrait' | 'cover'

  resources: ResourceMapping[] = []
  selectedId: number | null = null
  loading = false
  playingId: number | null = null
  audio: HTMLAudioElement | null = null
  uploadQueue: Array<{ file: File; name: string; progress: number; status: 'uploading' | 'success' | 'error' }> = []

  get isUploading(): boolean {
    return this.uploadQueue.some(item => item.status === 'uploading')
  }

  get selectedResource(): ResourceMapping | null {
    if (this.selectedId === null) return null
    return this.resources.find(r => r.id === this.selectedId) || null
  }

  handleOpen() {
    this.selectedId = null
    this.uploadQueue = []
    this.fetchResources()
  }

  handleClose() {
    this.stopPlay()
    this.$emit('update:show', false)
  }

  togglePlay(item: ResourceMapping) {
    if (this.playingId === item.id) {
      this.stopPlay()
      return
    }
    this.stopPlay()
    this.audio = new Audio(item.oss_url)
    this.audio.onended = () => { this.playingId = null }
    this.audio.play()
    this.playingId = item.id
  }

  stopPlay() {
    if (this.audio) {
      this.audio.pause()
      this.audio = null
    }
    this.playingId = null
  }

  async fetchResources() {
    this.loading = true
    try {
      const res = await getResources(this.gameId, this.fileType)
      const body = res.data || {}
      this.resources = Array.isArray(body) ? body : (body.data || [])
    } catch (e) {
      // Error handled by global interceptor
    } finally {
      this.loading = false
    }
  }

  handleSelect(item: ResourceMapping) {
    this.selectedId = item.id
  }

  handleBeforeUpload(file: File) {
    // 场景化限制（立绘 800KB），未登记的类型落到下方通用上限
    const sizeError = checkUploadSize(file, this.fileType)
    if (sizeError) {
      this.$message.error(sizeError)
      return false
    }
    // 前端体验兜底，不是与服务端"对齐"：服务端对上传大小没有硬上限
    // （ParseMultipartForm 的参数只是内存解析预算，不是请求体上限），实际受 nginx
    // client_max_body_size 约束。这里限制 200MB 只是避免用户等待很久后才收到失败提示。
    const MAX_UPLOAD_MB = 200
    const sizeMB = file.size / 1024 / 1024
    if (sizeMB > MAX_UPLOAD_MB) {
      this.$message.error(
        `文件 ${file.name} 大小 ${sizeMB.toFixed(1)}MB，超出上限 ${MAX_UPLOAD_MB}MB`
      )
      return false
    }
    return true
  }

  async handleUpload(options: any) {
    const file = options.file as File
    const queueItem: { file: File; name: string; progress: number; status: 'uploading' | 'success' | 'error' } = { file, name: file.name, progress: 0, status: 'uploading' }
    this.uploadQueue.push(queueItem)

    const formData = new FormData()
    formData.append('file', file)
    formData.append('game_id', String(this.gameId))
    formData.append('file_type', this.fileType)
    try {
      const res = await uploadResourceWithProgress(formData, (percent) => {
        queueItem.progress = percent
      })
      const data = res.data
      if (data.code === 0 && data.url) {
        const newResource: ResourceMapping = {
          id: data.id,
          game_id: this.gameId,
          display_name: file.name,
          oss_filename: '',
          file_type: this.fileType,
          oss_url: data.url,
          file_size: file.size
        }
        this.resources.unshift(newResource)
        this.selectedId = newResource.id
        queueItem.status = 'success'
      } else {
        queueItem.status = 'error'
        this.$message.error(data.msg || '上传失败')
      }
    } catch (e) {
      queueItem.status = 'error'
    }
  }

  async handleDelete(item: ResourceMapping) {
    try {
      await this.$confirm('确定删除该资源？', '提示', { type: 'warning' })
    } catch {
      return
    }
    try {
      await deleteResource(item.id)
      this.resources = this.resources.filter(r => r.id !== item.id)
      if (this.selectedId === item.id) this.selectedId = null
      this.$message.success('已删除')
    } catch {
      this.$message.error('删除失败')
    }
  }

  handleConfirm() {
    if (!this.selectedResource) return
    this.$emit('select', this.selectedResource)
    this.handleClose()
  }
}
</script>

<style scoped lang="scss">
.resource-uploader {
  min-height: 200px;
}

.resource-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 12px;
  max-height: 400px;
  overflow-y: auto;
  padding: 4px;
}

.resource-item {
  cursor: pointer;
  border: 2px solid transparent;
  border-radius: 6px;
  overflow: hidden;
  transition: border-color 0.2s;

  &:hover {
    border-color: #c0c4cc;
  }

  &.selected {
    border-color: #409eff;
  }
}

.resource-thumb {
  width: 100%;
  padding-top: 150%;
  position: relative;
  background: #f5f7fa;

  &--music {
    padding-top: 60%;
  }

  .delete-btn {
    position: absolute;
    top: 4px;
    right: 4px;
    font-size: 16px;
    color: #fff;
    background: rgba(0, 0, 0, 0.5);
    border-radius: 50%;
    padding: 4px;
    cursor: pointer;
    opacity: 0;
    transition: opacity 0.2s;
    z-index: 1;

    &:hover {
      background: #f56c6c;
    }
  }
}

.resource-item:hover .delete-btn {
  opacity: 1;
}

.thumb-image {
  object-fit: contain;
}

.thumb-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.thumb-music {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  color: #909399;
  cursor: pointer;

  .play-icon {
    position: absolute;
    top: 4px;
    left: 4px;
    font-size: 18px;
    color: #409eff;
  }

  &:hover {
    color: #409eff;
  }
}

.resource-name {
  padding: 4px 6px;
  font-size: 12px;
  color: #606266;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  background: #fff;
}

.empty-tip {
  text-align: center;
  color: #909399;
  padding: 20px 0;
}

.upload-divider {
  display: flex;
  align-items: center;
  margin: 16px 0;
  color: #909399;
  font-size: 13px;

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: #e4e7ed;
  }

  span {
    padding: 0 12px;
  }
}

.selected-preview {
  margin-top: 16px;
  text-align: center;

  .preview-image {
    max-width: 100%;
    max-height: 240px;
    border-radius: 6px;
    border: 1px solid #e4e7ed;
    object-fit: contain;
  }

  .preview-name {
    margin-top: 8px;
    font-size: 13px;
    color: #606266;
  }
}

.upload-area {
  ::v-deep .el-upload {
    width: 100%;
  }

  ::v-deep .el-upload-dragger {
    width: 100%;
    height: 100px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    .el-icon-upload {
      font-size: 36px;
      margin: 0 0 8px;
      line-height: 1;
    }

    .el-upload__text {
      font-size: 13px;
    }
  }
}

.upload-progress-list {
  margin-top: 12px;
}

.upload-progress-item {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}

.upload-file-name {
  font-size: 12px;
  color: #606266;
  width: 120px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex-shrink: 0;
}
</style>
